import { PriceChange } from '@toggle/toggle';
import { format, isWeekend } from 'date-fns';

import { getMarketHours } from '~/components/price-display-view/helpers/price-display-helpers';

import { DATE_FORMAT, PriceByDay } from './types';

interface MarketDayLookupArgs {
  days: string[];
  priceByDay: PriceByDay;
  targetDate: string;
}

const hasMarketHoursData = (priceData?: PriceChange): boolean =>
  !!priceData?.some(
    point => getMarketHours(new Date(point.time)).isMarketHours
  );

export const findPreviousMarketDay = ({
  days,
  priceByDay,
  targetDate,
}: MarketDayLookupArgs): string | undefined => {
  const exactIndex = days.indexOf(targetDate);
  const startIndex =
    exactIndex >= 0 ? exactIndex - 1 : days.findLastIndex(d => d < targetDate);

  for (let i = startIndex; i >= 0; i--) {
    if (hasMarketHoursData(priceByDay[days[i]])) {
      return days[i];
    }
  }

  return undefined;
};

export const findNextMarketDay = ({
  days,
  priceByDay,
  targetDate,
}: MarketDayLookupArgs): string | undefined => {
  const exactIndex = days.indexOf(targetDate);
  const startIndex =
    exactIndex >= 0 ? exactIndex + 1 : days.findIndex(d => d > targetDate);

  if (startIndex < 0) {
    return undefined;
  }

  for (let i = startIndex; i < days.length; i++) {
    if (hasMarketHoursData(priceByDay[days[i]])) {
      return days[i];
    }
  }

  return undefined;
};

/**
 * Finds the target date in the days array if it has market-hours data.
 * If the target date isn't in the array (e.g. weekend/holiday), falls
 * back to the closest preceding day with market-hours data.
 */
export const findCurrentMarketDay = ({
  days,
  priceByDay,
  targetDate,
}: MarketDayLookupArgs): string | undefined => {
  const exactIndex = days.indexOf(targetDate);

  if (exactIndex >= 0 && hasMarketHoursData(priceByDay[days[exactIndex]])) {
    return days[exactIndex];
  }

  return findPreviousMarketDay({ days, priceByDay, targetDate });
};

/**
 * Determines the chart day for a market catalyst - handles pre-market,
 * after-market, weekend, and regular market-hours publish scenarios.
 */
export const getMarketCatalystDay = ({
  days,
  priceByDay,
  reportingDateET,
  isPreMarket,
  isAfterMarket,
}: {
  days: string[];
  priceByDay: PriceByDay;
  reportingDateET: Date;
  isPreMarket: boolean;
  isAfterMarket: boolean;
}): string | undefined => {
  const targetDate = format(reportingDateET, DATE_FORMAT);
  const lookup = { days, priceByDay, targetDate };
  const isWeekendPublish = isWeekend(reportingDateET);

  if (isPreMarket) {
    return findCurrentMarketDay(lookup) ?? findPreviousMarketDay(lookup);
  }

  if (isAfterMarket || isWeekendPublish) {
    return findNextMarketDay(lookup) ?? findCurrentMarketDay(lookup);
  }

  return findCurrentMarketDay(lookup);
};
